import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from '../services/data.service';
import { LandingPageApiService } from '../services/landing-page-api.service';
import { BannerResponse } from '../../models/user-info.model';
import { TranslatePipe } from '@ngx-translate/core';
import { NgShuiModule } from "@shui/angular";
import { FilterCriteriaService } from '../services/filter-criteria.service';
import { CommonModule } from '@angular/common';
import { CreateQueryMode, CreateQueryTemplate } from '../../models/filter-condition.model';
import { DEFAULT_CREATE_QUERY_MODE, EMPTY_TEMPLATE, QueryType } from '../constants/constants';

@Component({
  selector: 'app-landing-page',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, TranslatePipe, NgShuiModule],
  styleUrl: './landing-page.component.scss',
  standalone: true,
  templateUrl: './landing-page.component.html'
})
export class LandingPageComponent implements OnInit {

  constructor(
    private router: Router,
    private dataService: DataService,
    private landingPageService: LandingPageApiService,
    private filterCriteriaService: FilterCriteriaService,
  ) {}
  bannerTitle: string = 'IB Search Engine';
  bannerDescription: string = 'Create your own filter file';
  isLoadingBanner: boolean = false;
  showCreateQueryModal: boolean = false;
  selectedCreateQueryMode: CreateQueryMode = DEFAULT_CREATE_QUERY_MODE;
  myPersonalTemplates: CreateQueryTemplate[] = [];
  myPublicTemplates: CreateQueryTemplate[] = [];
  selectedTemplate: CreateQueryTemplate | null = null;
  isLoadingMyTemplates: boolean = false;
  myFavoriteQueries: CreateQueryTemplate[] = [];
  selectedFavQuery: CreateQueryTemplate | null = null;
    ngOnInit(): void {
    this.loadBanner();
  }

  loadBanner(): void {
    this.isLoadingBanner = true;
    this.landingPageService.getBanner().subscribe({
      next: (response: BannerResponse) => {
        this.bannerTitle = response.title;
        this.bannerDescription = response.description;
        this.isLoadingBanner = false;
      },
      error: (error) => {
        console.error('Error loading banner:', error);
        this.isLoadingBanner = false;
      }
    });
  }

  createQuery(): void {
    this.showCreateQueryModal = true;
    this.selectedCreateQueryMode = DEFAULT_CREATE_QUERY_MODE;
    this.selectedTemplate = { ...EMPTY_TEMPLATE };
    this.loadMyTemplates();
  }

  closeCreateQueryModal(): void {
    this.showCreateQueryModal = false;
  }

  onCreateQueryModeChange(mode: CreateQueryMode): void {
    this.selectedCreateQueryMode = mode;
    if (mode !== 'personal_template' && mode !== 'public_template' && mode !== 'fav_query') {
      this.selectedTemplate = { ...EMPTY_TEMPLATE };
    }
  }

  onTemplateSelectionChange(template: CreateQueryTemplate): void {
    this.selectedTemplate = template ? { ...template } : { ...EMPTY_TEMPLATE };
  }

  onQuerySelectionChange(query: CreateQueryTemplate): void {
    this.selectedFavQuery = query ? { ...query } : { ...EMPTY_TEMPLATE };
  }

  canConfirmCreateQuery(): boolean {
    if (!this.selectedCreateQueryMode.includes('template') && this.selectedCreateQueryMode !== 'fav_query') {
      return true;
    }
    const selectedGUID = this.selectedCreateQueryMode === 'fav_query' ? this.selectedFavQuery?.guid : this.selectedTemplate?.guid;
    return !!selectedGUID;
  }

  confirmCreateQuery(): void {
    this.dataService.selectedGUID = '';
    this.dataService.selectedQuery = null;
    this.dataService.selectedFilterName = '';
    this.dataService.ownerTemplateChoice = 'edit';

    if (this.selectedCreateQueryMode === 'empty') {
      this.dataService.queryCreationMode = 'empty';
      this.showCreateQueryModal = false;
      this.router.navigate(['/filter-criteria', '']);
      return;
    }

    if (this.selectedCreateQueryMode === 'default') {
      this.dataService.queryCreationMode = 'default';
      this.showCreateQueryModal = false;
      this.router.navigate(['/filter-criteria', '']);
      return;
    }

    if (this.selectedCreateQueryMode === 'fav_query') {
      const selectedFavQuery = this.myFavoriteQueries.find((query) => query.guid === this.selectedFavQuery?.guid);
      if (!selectedFavQuery) {
        return;
      }

      this.dataService.selectedGUID = selectedFavQuery.guid;
      this.dataService.selectedFilterName = selectedFavQuery.name;
      this.dataService.queryCreationMode = 'fav_query';
      this.showCreateQueryModal = false;
      this.router.navigate(['/filter-criteria', selectedFavQuery.guid]);
      return;
    }


    if (!this.selectedTemplate?.guid) {
      return;
    }

    const selectedTemplate = this.getActiveTemplateCollection().find((template) => template.guid === this.selectedTemplate?.guid);
    if (!selectedTemplate) {
      return;
    }

    this.dataService.queryCreationMode = 'template';
    this.dataService.ownerTemplateChoice = 'edit';
    this.dataService.selectedGUID = selectedTemplate.guid;
    this.dataService.selectedFilterName = selectedTemplate.name;
    this.showCreateQueryModal = false;
    this.router.navigate(['/filter-criteria', selectedTemplate.guid]);
  }

  private getActiveTemplateCollection(): CreateQueryTemplate[] {
    return this.selectedCreateQueryMode === 'public_template'
      ? this.myPublicTemplates
      : this.myPersonalTemplates;
  }

  private loadMyTemplates(): void {
    this.isLoadingMyTemplates = true;
    this.filterCriteriaService.getAllQuery().subscribe({
      next: (response) => {
        const allTemplates = response?.templateFilter || [];

        const personalTemplates = allTemplates
          .filter((template: any) => {
            const rawType = String(template?.type || '').toLowerCase();
            return rawType === QueryType.Template.toLowerCase() || rawType === 'template' || rawType === 'personal';
          })
          .map((template: any) => ({
            guid: template.guid || '',
            name: template.name || '',
            type: 'Personal' as const,
          }));

        const publicTemplates = allTemplates
          .filter((template: any) => {
            const rawType = String(template?.type || '').toLowerCase();
            return rawType === QueryType.Public_Template.toLowerCase() || rawType === 'public' || rawType === 'public_template';
          })
          .map((template: any) => ({
            guid: template.guid || '',
            name: template.name || '',
            type: 'Public' as const,
          }));

        const seenPersonalGuids = new Set<string>();
        this.myPersonalTemplates = [...personalTemplates]
          .filter((template) => {
            if (!template.guid || seenPersonalGuids.has(template.guid)) {
              return false;
            }
            seenPersonalGuids.add(template.guid);
            return true;
          })
          .sort((first, second) => first.name.localeCompare(second.name));

        const seenPublicGuids = new Set<string>();
        this.myPublicTemplates = [...publicTemplates]
          .filter((template) => {
            if (!template.guid || seenPublicGuids.has(template.guid)) {
              return false;
            }
            seenPublicGuids.add(template.guid);
            return true;
          })
          .sort((first, second) => first.name.localeCompare(second.name));

        this.myFavoriteQueries = response?.query?.filter((query: any) => query.isFavourite).map((query: any) => ({
          guid: query.guid || '',
          name: query.name || '',
          type: 'Standard' as const,
        })).sort((first: any, second: any) => first.name.localeCompare(second.name));
        this.isLoadingMyTemplates = false;
      },
      error: (error) => {
        console.error('Error loading templates:', error);
        this.myPublicTemplates = [];
        this.myPersonalTemplates = [];
        this.isLoadingMyTemplates = false;
      }
    });
  }

  private resolveTemplateType(
    template: any,
    fallbackType: 'Public' | 'Personal',
  ): 'Public' | 'Personal' {
    const rawType = String(template?.type || '').toLowerCase();
    if (
      rawType === QueryType.Public_Template.toLowerCase() ||
      rawType === 'public' ||
      rawType === 'public template' ||
      rawType === 'public_template'
    ) {
      return 'Public';
    }

    if (
      rawType === QueryType.Template.toLowerCase() ||
      rawType === 'template' ||
      rawType === 'personal' ||
      rawType === 'personal template'
    ) {
      return 'Personal';
    }

    return fallbackType;
  }

  viewQueries(): void {
    this.router.navigate(['/view-query']);
  }

  manageTemplates(): void {
    this.router.navigate(['/manage-template']);
  }
}
